import { useCallback, useEffect, useState } from "react";

import {
  exportToSvg
} from ".";

import type { BinaryFiles } from "../utils/types";
import type {
  SchemadrawElement,
} from "../element/types";

import {
  getNonDeletedElements,
} from "../element";

import {
  getRemoteLibraryElemsByID,
} from "../data/library";

import { loadScene, loadTemplate } from "../data";
import { animateSvg } from "../utils/animate";


export const useLoadSvg = () => {
  const [loading, setLoading] = useState(true);
  const [loadedSvgList, setLoadedSvgList] = useState<
    {
      svg: SVGSVGElement;
      finishedMs: number;
    }[]
  >([]);

  const loadDataList = useCallback(
    async (
      dataList: {
        elements: readonly SchemadrawElement[];
        appState: Parameters<typeof exportToSvg>[0]["appState"];
        files: BinaryFiles;
      }[],
      inSequence?: boolean
    ) => {
      const hash = window.location.hash.slice(1);
      const searchParams = new URLSearchParams(hash);
      const options = {
        startMs: undefined as number | undefined,
        pointerImg: searchParams.get("pointerImg") || undefined,
        pointerWidth: searchParams.get("pointerWidth") || undefined,
        pointerHeight: searchParams.get("pointerHeight") || undefined,
      };
      const svgList = await Promise.all(
        dataList.map(async (data) => {
          const elements = getNonDeletedElements(data.elements);
          const svg = await exportToSvg({
            elements,
            files: data.files,
            appState: data.appState,
            exportPadding: 30,
          });
          const result = animateSvg(svg, elements, options);
          if (inSequence) {
            options.startMs = result.finishedMs;
          }
          return { svg, finishedMs: result.finishedMs };
        })
      );
      setLoadedSvgList(svgList);
      return svgList;
    },
    []
  );

  useEffect(() => {
    (async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const did = searchParams.get("did");
      const tid = searchParams.get("tid");
      const lid = searchParams.get("lid");
      const privateKey = searchParams.get("key");
      const jsonBackendMatch = window.location.hash.match(
        /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
      );

      if (jsonBackendMatch || did) {
        // try design
        if (jsonBackendMatch) {
          let data = await loadScene(
            jsonBackendMatch[1],
            jsonBackendMatch[2],
            null,
          );
          const [{ svg, finishedMs }] = await loadDataList([data]);
          if (searchParams.get("autoplay") === "no") {
            svg.setCurrentTime(finishedMs);
          }
        } else if (did) {
          let data = await loadScene(
            did,
            privateKey,
            null
          );
          const [{ svg, finishedMs }] = await loadDataList([data]);
          if (searchParams.get("autoplay") === "no") {
            svg.setCurrentTime(finishedMs);
          }
        }
      } else if (tid) {
        let data = await loadTemplate(
          tid,
          privateKey,
          null
        );
        const [{ svg, finishedMs }] = await loadDataList([data]);
        if (searchParams.get("autoplay") === "no") {
          svg.setCurrentTime(finishedMs);
        }
      } else if (lid) {
        // try library
        if (lid) {
          const dataList = await getRemoteLibraryElemsByID(lid);
          const svgList = await loadDataList(
            dataList.map((elements) => ({ elements, appState: {}, files: {} })),
            searchParams.has("sequence")
          );
          if (searchParams.get("autoplay") === "no") {
            svgList.forEach(({ svg, finishedMs }) => {
              svg.setCurrentTime(finishedMs);
            });
          }
        }
      }
      setLoading(false);
    })();
  }, [loadDataList]);

  return { loading, loadedSvgList, loadDataList };
};